// @ts-strict-ignore
import { ErrorBoundary as SentryErrorBoundary, setUser, setContext } from "@sentry/react";
import type { ErrorBoundaryProps, FallbackRender } from "@sentry/react";
import type { Scope } from "@sentry/types";
import { useMemo, useEffect, useRef, type ReactNode } from "react";

import { useSharedGlobalConfig } from "@shared/hooks/useSharedGlobalConfig/useSharedGlobalConfig";
import type { To } from "@shared/react-router-dom/react-router-dom";
import { useLocation } from "@shared/react-router-dom/react-router-dom";
import type { Flipper } from "@shared/types/flipper";
import { Fallback } from "@unauthenticated/signupSignin/components/ErrorBoundary/Fallback/Fallback";

interface Props {
  to?: To;
  flippers: Flipper[];
  pageVersion?: "prac_by_default_with_sso" | "prac_by_default" | "react_with_sso" | "react";
  storeId?: string;
  roleId?: string;
  children: ReactNode;
}

const ErrorBoundary = ({ children, to, flippers, pageVersion, roleId, storeId }: Props) => {
  const { pathname } = useLocation();
  const { country } = useSharedGlobalConfig();
  const errorBoundaryRef = useRef<SentryErrorBoundary>(null);

  // This is intentionally useMemo instead of useEffect
  // Reason for this is useMemo is invoked earlier in the rendering process
  // Allowing us to capture errors that occurred during the render
  const unusedContextInitialization = useMemo(() => {
    setUser({ roleId, storeId });
    setContext("flippers", { flippers });

    return null;
  }, []);

  /**
   * Reset error boundary when user attempts to navigate away from the page. This could be due to:
   *    - Navigating back to the previous page via browser navigation actions
   *    - Actioning on the error page's "Back to Home" link
   */
  useEffect(() => {
    if (errorBoundaryRef.current?.state.error) errorBoundaryRef.current.resetErrorBoundary();
  }, [pathname]);

  const renderFallback: FallbackRender = () => {
    return <Fallback to={to} />;
  };

  const handleCapture: ErrorBoundaryProps["beforeCapture"] = (scope: Scope) => {
    scope.setTags({ spa: "auth", country, pageVersion });
  };

  return (
    <SentryErrorBoundary
      ref={errorBoundaryRef}
      showDialog={false}
      fallback={renderFallback}
      beforeCapture={handleCapture}
    >
      {children}
    </SentryErrorBoundary>
  );
};

export { ErrorBoundary };
